import { useStore } from '@tanstack/react-form'
import clsx from 'clsx'
import debug from 'debug'
import { ReactElement, useRef } from 'react'
import { getValue } from 'src/utils/form/form'

import ErrorMessage from './error-message'
import { VendiaFormApi } from './form'
import Label from './label'
import LabelNested from './label-nested'
import { FieldSetProps, FormInputProps } from './types'

const logger = debug('app:field-set')

const FieldSet = <T extends HTMLElement = HTMLInputElement>({
  name,
  wrapperClassName,
  labelClassName,
  children,
  useNestedLabel,
  nestedLabelRequiresOffset,
  label,
  description,
  form,
  isArrayField,
  arrayFieldName,
  arrayFieldIndex,
  fieldNameKey,
  labelRef,
  inputRef,
  nestedLabelRef,
  errorRef,
  'data-testid': dataTestId,
}: FieldSetProps &
  Omit<FormInputProps, 'children'> & {
    form: VendiaFormApi<any>
    fieldNameKey?: string
    children: (hasErrors: boolean) => ReactElement
  }) => {
  if (!children) {
    throw new Error(`Form input was not provided for ${name}`)
  }
  if (useNestedLabel && !label) {
    throw new Error(`Label is required for nested label`)
  }
  if (isArrayField && !arrayFieldName) {
    throw new Error(`Array field name is required for array field`)
  }
  if (isArrayField && arrayFieldIndex === undefined) {
    throw new Error(`Array field index is required for array field`)
  }

  // This will detect the position of the input element and adjust the nested label position
  // to allow for addition left content
  const containerRef = useRef<HTMLDivElement>(null)
  let nestedLabelOffset = 0
  if (inputRef && useNestedLabel && nestedLabelRequiresOffset) {
    const inputLeft = inputRef.current?.getBoundingClientRect().left
    const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0
    if (inputLeft) {
      nestedLabelOffset = inputLeft - containerLeft
    }
  }

  const hasErrors = form
    ? useStore(form.store, (s: any) => {
        const { errors, isDirty, isBlurred } = s.fieldMeta[name] ?? {}
        const { isSubmitted, isSubmitting, submissionAttempts } = s
        // logger('hasErrors', { name, errors, isDirty, isBlurred, isSubmitted, isSubmitting, submissionAttempts, form })
        return errors?.length > 0 && (isDirty || isBlurred || isSubmitted || submissionAttempts > 0)
      })
    : false

  const fieldValue = form
    ? useStore(form.store, (state: any) => {
        let path = name
        if (isArrayField) {
          path = fieldNameKey
            ? `${arrayFieldName}.${arrayFieldIndex}.${fieldNameKey}`
            : `${arrayFieldName}.${arrayFieldIndex}`
        }
        return getValue(state.values as any, path)
      })
    : undefined
  const hasDescription = Boolean(description)

  // Check if value is actually set
  const isValueSet = fieldValue != null && 
    fieldValue !== '' && 
    (typeof fieldValue !== 'number' || !isNaN(fieldValue))

  // Don't show nested label if value is empty
  const shouldShowNestedLabel = useNestedLabel && isValueSet

  return (
    <div className={clsx('relative mb-3 w-full min-w-0', wrapperClassName)} ref={containerRef}>
      <Label
        ref={labelRef}
        name={name}
        label={label}
        useNestedLabel={useNestedLabel}
        hasDescription={hasDescription}
        className={labelClassName}
        description={description}
        data-testid={dataTestId}
      />
      <LabelNested
        label={label}
        useNestedLabel={useNestedLabel}
        showNestedLabel={shouldShowNestedLabel}
        nestedLabelOffset={nestedLabelOffset}
        ref={nestedLabelRef}
      />
      {children(hasErrors)}
      <ErrorMessage name={name} form={form} ref={errorRef} hasErrors={hasErrors} />
    </div>
  )
}

export default FieldSet
